import React from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {Box, Colors, Container, Text} from "@bounce/shared/ui";
import {MenuItem, Select, TextField} from "../../../ui/components";
import {FieldLabel, FormCard, INPUT_SX} from "./FormCard";
import SegmentedToggle from "./SegmentedToggle";
import {
  CADENCE_UNITS,
  CADENCE_UNIT_LABELS,
  PROMO_WEEKDAYS,
  PROMO_WINDOWS,
  START_MODES,
  START_MODE_LABELS,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT_LABELS,
  WINDOW_LABELS,
} from "./constants";
import {parseNumberInput} from "./helpers";
import {PromoFormValues} from "./schema";

const WINDOW_HINT = "How long the promo runs";
const RELATIVE_WINDOW_NOTE = "Relative windows start when the user enters the promo, not at launch.";
const START_ON_DATE_HELP = "Goes live on the date you pick.";
const DATE_INPUT_WIDTH = 220;
const CADENCE_EVERY_WIDTH = 80;
const REPEAT_OPTIONS = [
  {value: "off", label: "No repeat"},
  {value: "on", label: "Every"},
] as const;
const NO_REPEAT_HELP = "Each account enters the promo once.";

const startHelp = (startMode: PromoFormValues["start_mode"], weekday: PromoFormValues["start_weekday"]) =>
  startMode === "date"
    ? START_ON_DATE_HELP
    : `Goes live on the next ${WEEKDAY_LABELS[weekday]}. Accounts enter only on ${WEEKDAY_LABELS[weekday]}s.`;

const repeatHelp = (
  repeats: boolean,
  cadenceEvery: PromoFormValues["cadence_every"],
  cadenceUnit: PromoFormValues["cadence_unit"]
) =>
  repeats
    ? `Each account re-enters the promo window every ${cadenceEvery ?? "…"} ${CADENCE_UNIT_LABELS[cadenceUnit]}.`
    : NO_REPEAT_HELP;

const WindowCard = () => {
  const {control} = useFormContext<PromoFormValues>();
  const promoWindow = useWatch({control, name: "window"});
  const startMode = useWatch({control, name: "start_mode"});
  const startWeekday = useWatch({control, name: "start_weekday"});
  const repeats = useWatch({control, name: "repeats"});
  const cadenceEvery = useWatch({control, name: "cadence_every"});
  const cadenceUnit = useWatch({control, name: "cadence_unit"});
  const isCustomWindow = promoWindow === "custom";

  return (
    <FormCard title="Window" hint={WINDOW_HINT}>
      <Container row fullWidth sx={{gap: 1.5, alignItems: "center", flexWrap: "nowrap"}}>
        <Box sx={{width: DATE_INPUT_WIDTH, flexShrink: 0}}>
          <Controller
            name="window"
            control={control}
            render={({field}) => (
              <Select {...field} fullWidth size="small" sx={INPUT_SX}>
                {PROMO_WINDOWS.map((window) => (
                  <MenuItem key={window} value={window}>
                    {WINDOW_LABELS[window]}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Box>
        <Text size={13} sx={{color: Colors.grey._600}}>
          {RELATIVE_WINDOW_NOTE}
        </Text>
      </Container>

      <Box>
        <Controller
          name="start_mode"
          control={control}
          render={({field}) => (
            <SegmentedToggle
              value={field.value}
              options={START_MODES.map((value) => ({value, label: START_MODE_LABELS[value]}))}
              onChange={field.onChange}
            />
          )}
        />
        <Container row sx={{gap: 2, mt: 1.5, alignItems: "flex-start", flexWrap: "wrap"}}>
          <Box sx={startMode === "date" ? {width: DATE_INPUT_WIDTH} : undefined}>
            <FieldLabel required>Start</FieldLabel>
            {startMode === "date" ? (
              <Controller
                key="starts_at"
                name="starts_at"
                control={control}
                render={({field: {ref, ...field}, fieldState}) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    type="date"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    sx={INPUT_SX}
                  />
                )}
              />
            ) : (
              <Controller
                key="start_weekday"
                name="start_weekday"
                control={control}
                render={({field}) => (
                  <SegmentedToggle
                    value={field.value}
                    options={PROMO_WEEKDAYS.map((value) => ({value, label: WEEKDAY_SHORT_LABELS[value]}))}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </Box>
          <Box sx={{width: DATE_INPUT_WIDTH}}>
            <FieldLabel required={isCustomWindow}>End</FieldLabel>
            {isCustomWindow ? (
              <Controller
                name="ends_at"
                control={control}
                render={({field: {ref, ...field}, fieldState}) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    type="date"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    sx={INPUT_SX}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                disabled
                value={`Set by duration (${WINDOW_LABELS[promoWindow]})`}
                sx={INPUT_SX}
              />
            )}
          </Box>
        </Container>
        <Text size={12} sx={{color: Colors.grey._600, mt: 0.75}}>
          {startHelp(startMode, startWeekday)}
        </Text>
      </Box>

      <Box>
        <FieldLabel>Repeat</FieldLabel>
        <Container row fullWidth sx={{gap: 1.5, alignItems: "center", flexWrap: "nowrap"}}>
          <Controller
            name="repeats"
            control={control}
            render={({field}) => (
              <SegmentedToggle
                value={field.value ? "on" : "off"}
                options={[...REPEAT_OPTIONS]}
                onChange={(next) => field.onChange(next === "on")}
              />
            )}
          />
          {repeats && (
            <>
              <Box sx={{width: CADENCE_EVERY_WIDTH}}>
                <Controller
                  name="cadence_every"
                  control={control}
                  render={({field: {ref, onChange, value, ...field}, fieldState}) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      type="number"
                      size="small"
                      value={value ?? ""}
                      onChange={(event) => onChange(parseNumberInput(event.target.value))}
                      error={!!fieldState.error}
                      sx={INPUT_SX}
                    />
                  )}
                />
              </Box>
              <Controller
                name="cadence_unit"
                control={control}
                render={({field}) => (
                  <Select {...field} size="small" sx={INPUT_SX}>
                    {CADENCE_UNITS.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {CADENCE_UNIT_LABELS[unit]}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </>
          )}
        </Container>
        <Text size={12} sx={{color: Colors.grey._600, mt: 0.75}}>
          {repeatHelp(repeats, cadenceEvery, cadenceUnit)}
        </Text>
      </Box>
    </FormCard>
  );
};

export default WindowCard;
